import type { FieldValues, UseFormGetValues, UseFormSetError } from 'react-hook-form';

import * as z from 'zod';

import { getFieldValue } from '@/services/forms';
import { compareToField, includeAtLeastOne } from '@/services/validation';
import { accumulateVisibleValues } from '@/services/questions';

export type GetValuesFunction = UseFormGetValues<FieldValues> | ( ( path: string | string[] ) => string | string[] );

function getComparisonError( comparisonType: 'greater' | 'less' | 'equal', targetSystemId: string, customError?: string ) {
	const separator = comparisonType === 'equal' ? 'to' : 'than';

	return customError || `This value must be ${ comparisonType } ${ separator } ${ targetSystemId }`;
}

export function comparisonValidatorFunction( question: any, validator: ValidationFieldComparison, v: string, isRequired: boolean, getValues: GetValuesFunction ) {
	if ( !isRequired && !v ) {
		return true;
	}
	
	const thisValidator = validator as ValidationFieldComparison;

	const targetId = [ question.idPrefix || question.prefix, thisValidator.targetSystemId ].filter( a => a ).join( '.' );

	const targetFieldValue = getFieldValue( getValues( targetId ) );
	const currentFieldValue = getFieldValue( v );
	const comparisonType = thisValidator.comparisonType;
	
	return compareToField( currentFieldValue, targetFieldValue, comparisonType ) || getComparisonError( comparisonType, thisValidator.targetSystemId, thisValidator.customError );
}

export function atLeastOneValidatorFunction( atLeastOneValidators: ValidationRequireAtLeastOne[], question: any, v: string, getValues: GetValuesFunction, setError: UseFormSetError<FieldValues> ) {
	const validator = atLeastOneValidators[ 0 ] as ValidationRequireAtLeastOne;
	
	return includeAtLeastOne( validator.targetSystemIds, getValues, setError, validator.customError, question.idPrefix );
}

export function selectOneFieldComparisonValidatorFunction( validator: SelectOneFieldComparison, question: any, getValues: GetValuesFunction ) {
	// first, check to see if at least one of them is selected
	const thisValidator = validator as SelectOneFieldComparison;
	const allIds = thisValidator.selectOneFields.map( obj => [ question.idPrefix, obj.systemId ].filter( a => a ).join( '.' ) );
	const currentValues = getValues( allIds ) as string[];

	const setValues = allIds.map( ( id, index ) => {
		return {
			id: id,
			compareToId: thisValidator.selectOneFields[ index ].compareToId,
			comparisonType: thisValidator.selectOneFields[ index ].comparisonType,
			customError: thisValidator.selectOneFields[ index ].customError,
			value: getFieldValue( currentValues[ index ] )
		};
	});

	const filteredSetValues = setValues.filter( a => a.value && a.value !== '0' );

	if ( filteredSetValues.length === 0 || filteredSetValues.length > 1 ) {
		return thisValidator.selectOneCustomError || 'Must select one value';
	}

	// then check to see if the set values compare correctly
	const comparisons = setValues.filter( a => {
		return a.value && a.value !== '0'
	}).map( obj => {
		// If the comparison ID is just the prefix, then we don't actually
		// need to do a comparison. In the future, we should probably adjust
		// the prefixing logic.
		if ( !obj.compareToId || obj.compareToId === question.prefix ) {
			return {
				...obj,
				comparison: true
			};
		}

		const comparisonId = [ question.idPrefix, obj.compareToId ].filter( a => a ).join( '.' );

		const sourceValue = obj.value;
		const targetValue = getFieldValue( getValues( comparisonId ) );

		const comparisonType = obj.comparisonType;

		return {
			...obj,
			comparison: compareToField( sourceValue, targetValue, comparisonType )
		};
	}).filter( a => !a.comparison );

	if ( comparisons.length > 0 ) {
		const firstComparison = comparisons[ 0 ];

		return getComparisonError( firstComparison.comparisonType, firstComparison.compareToId, firstComparison.customError );
	} else {
		return true;
	}
}

// export function requireAdditionalForm( validator: RequireAdditionalForm, question: any, getValues: GetValuesFunction, cppData: any ) {
// 	const matchesCppType = validator.cppType === 'all' || validator.cppType === 'amend' && cppData.isCppMidterm || validator.cppType === 'new' && !cppData.isCppMidterm;

// 	if ( !matchesCppType ) {
// 		return true;
// 	}

// 	const questionValue = getValues( question.systemId );
// 	const valueComparisonMatches = compareToField( validator.value, questionValue as string, validator.comparisonType );

// 	if ( valueComparisonMatches ) {
// 		return [ ...cppData.applications, ...cppData.questionnaires ].find( a => a.id === validator.formId ) || validator.errorMessage;
// 	}

// 	return true;
// }

function getFullSystemId( question: any ) {
	let tempId;

	if ( question.systemId.startsWith( question.prefix ) ) {
		tempId = question.systemId;
		let newSystemId = question.systemId.split( '. ' );

		if ( newSystemId.length > 1 ) {
			newSystemId.pop();

			if ( !tempId.startsWith( newSystemId.join( '.' ) ) ) {
				newSystemId.push( tempId );

				tempId = newSystemId.join( '.' );
			}
		}
	}

	return tempId;
}

export function generateZodSchema( formData: any, cppData: any, getValues: any, setError: () => void ) {
	const questions: any[] = [];

	accumulateVisibleValues( formData.questions, cppData, getValues, ( question: any ) => {
		const duplicatedObject = Object.assign( {}, question );

		if ( duplicatedObject.questions ) {
			delete duplicatedObject.questions;
		}

		if ( duplicatedObject.additionalQuestions ) {
			delete duplicatedObject.additionalQuestions;
		}

		if ( question.systemId ) {
			questions.push( duplicatedObject );
		}
	});

	function getFieldSchema( question: any, schema: any ) {
		// let fieldSchema: z.ZodType<any>;
		let fieldSchema: any;

		switch ( question.fieldType ) {
			case 'Checkbox':
				const checkboxSchema = z.union([ z.string(), z.array( z.string() ), z.boolean() ]);
				
				if ( !question.isRequired ) {
					fieldSchema = z.nullable( z.optional( checkboxSchema ) );
				}

				fieldSchema = fieldSchema.superRefine( ( val: any , ctx: any ) => {
					if ( val === undefined ) {
						ctx.addIssue({
							code: 'custom',
							message: 'Please select at least one option',
							input: val
						});
					}
				});

				break;
			
			case 'Text':
				fieldSchema = z.string();

				// if questions have a minimumValue or maximumValue, they are going to be
				// string representations of numbers, so we need to coerce them into numbers
				if ( question.minimumValue || question.maximumValue ) {
					fieldSchema = z.coerce.number();
				} else {
					fieldSchema = z.string();
				}

				if ( question.maxLen ) {
					fieldSchema = fieldSchema.max( question.maxLen, `This field may not contain more than ${ question.maxLen } characters` )
				}

				if ( question.minimumValue ) {
					fieldSchema = fieldSchema.min( question.minimumValue, `This value must be at least ${ question.minimumValue }` )
				}

				if ( question.maximumValue ) {
					fieldSchema = fieldSchema.max( question.maximumValue, `This value may not be greater than ${ question.maximumValue }` );
				}

				break;
			
			case 'Select':
				// fieldSchema = z.object({
				// 	label: z.string(),
				// 	value: z.string()
				// });
				fieldSchema = z.string();

				break;
			
			case 'YesNo':
				fieldSchema = z.enum([ 'yes', 'no', 'na' ]);

				break;

			case 'AdditionalApplicationsChecklist':
				fieldSchema = z.array( z.string() );
				break;
			
			// we don't need to validate this
			case 'Supporting Business':
				return;
			
			case 'MultiItem':
				// we can skip MultiItem because the validation rules
				// apply to the row questions which are already accumulated
				return;
			
			case 'MailingAddress':
				fieldSchema = z.object({
					number: z.nullable( z.string().max( 15 ).optional() ),
					name: z.nullable( z.string().max( 30 ).optional() ),
					poBox: z.nullable( z.string().max( 9 ).optional() ),
					city: z.string().max( 20 ),
					state: z.string().max( 2 ),
					zip: z.string().max( 12 )
				});

				break;
			
			case 'Applicant':
				fieldSchema = z.object({
					firstName: z.nullable( z.string().max( 30 ).optional() ),
					middleName: z.nullable( z.string().max( 40 ).optional() ),
					lastName: z.nullable( z.string().max( 40 ).optional() ),
					otherNamedInsureds: z.nullable( z.string().max( 5250 ).optional() ),
					dba: z.nullable( z.string().max( 5250 ).optional() ),
					contactPerson: z.nullable( z.string().max( 40 ).optional() )
				});

				break;
			
			case 'TableEmbeddedForm':
				// we can skip TableEmbeddedForm because the validation rules
				// apply to the subform questions which are already accumulated
				return;
			
			case 'SyncedAddress':
				// skipping this for now
				return;
			
			case 'Mortgagee':
				// skipping this for now
				return;
				// fieldSchema = z.object({
				// 	businessName: z.nullable( z.string().max( 40 ).optional() ),
				// 	number: z.nullable( z.string().max( 15 ).optional() ),
				// 	name: z.nullable( z.string().max( 30 ).optional() ),
				// 	poBox: z.nullable( z.string().max( 9 ).optional() ),
				// 	city: z.string().max( 20 ),
				// 	state: z.string().max( 2 ),
				// 	zip: z.string().max( 12 ),
				// 	natureOfInterest: z.string().max( 400 )
				// });

				// break;
			
			default:
				fieldSchema = z.string();
		}

		// since we're using superRefine to customize checkbox error messages, we don't need to do this for checkboxes
		if ( !question.required && question.fieldType !== 'Checkbox' ) {
			fieldSchema = z.nullable( z.optional( fieldSchema ) );
		}

		// check for select one field comparison validation
		const selectOneValidators = ( question?.validation || [] ).filter( ( validator: any ) => {
			return validator.fieldType === 'SelectOneFieldComparison';
		});

		// check for at least one is selected validation
		const atLeastOneValidators = ( question.validation || [] ).filter( ( validator: any ) => {
			return validator.fieldType === 'RequireAtLeastOne';
		});

		// check for field comparison validation
		const comparisonValidators = ( question.validation || [] ).filter( ( validator: any ) => {
			return validator.fieldType === 'FieldComparison';
		});

		// if we have any of the custom validators, we have to adjust how we set up the field validator
		if ( selectOneValidators.length > 0 || atLeastOneValidators.length > 0 || comparisonValidators.length > 0 ) {
			fieldSchema = z;
		}

		if ( selectOneValidators.length > 0 ) {
			selectOneValidators.forEach( ( validator: any ) => {
				fieldSchema = fieldSchema.custom( ( val: unknown ) => {
					const remappedValidator = {
						...validator,
						selectOneFields: validator.selectOneFields.map( ( fields: any ) => ({
							...fields,
							systemId: [ question.prefix, fields.systemId ].filter( a => a ).join( '.' ),
							compareToId: [ question.prefix, fields.compareToId ].filter( a => a ).join( '.' )
						}))
					};

					return selectOneFieldComparisonValidatorFunction( remappedValidator, question, getValues ) === true ? true : false;
				}, validator.customError || 'Please review this value' );
			});
		}
		
		if ( atLeastOneValidators.length > 0 ) {
			fieldSchema = fieldSchema.custom( ( val: unknown ) => {
				return atLeastOneValidatorFunction( atLeastOneValidators, question, val as string, getValues, setError ) === true ? true : false;
			}, atLeastOneValidators[ 0 ].customError || 'Please review this value' );
		}

		if ( comparisonValidators.length > 0 ) {
			comparisonValidators.forEach( ( validator: any ) => {
				fieldSchema = fieldSchema.custom( ( val: unknown ) => {
					return comparisonValidatorFunction( question, validator, val as string, question.required, getValues ) === true ? true : false;
				}, validator.customError || 'Please review this value' );
			});
		}

		if ( fieldSchema ) {
			if ( question.fieldType === 'Select' ) {
				schema[ question.systemId + '.value' ] = fieldSchema;
			} else {
				schema[ question.systemId ] = fieldSchema;
			}
		}
	}

	const schema: Record<string, z.ZodType<any>> = {};

	for ( const question of questions ) {
		getFieldSchema( question, schema );
	}

	const combinedSchema = z.object( schema );
	
	return {
		zodSchema: combinedSchema,
		questionMap: questions.map( question => ({
			systemId: question.systemId,
			label: question.label,
			parentForm: question.parentForm
		}))
	};
}
